import { Button, Flex, Text, Title } from "@mantine/core";
import Image from "next/image";
import Link from "next/link";
import { useEffect } from "react";
import { twMerge } from "tailwind-merge";

import {
  ScreenerBuilderWrapper,
  type ScreenerBuilderWrapperProps,
} from "@/components/screener-builder/common";
import {
  NOTIFICATION_CLASS_NAMES,
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";

interface GenerationFailedProps {
  wrapperProps: ScreenerBuilderWrapperProps;
}

const SHARED = {
  title: "Unable to generate interview",
  content:
    "Looks like something went wrong—you can archive this interview and try again.",
  link: {
    href: "/",
    text: "Back to all interviews",
  },
};

export const GenerationFailed = ({ wrapperProps }: GenerationFailedProps) => {
  useEffect(() => {
    const { hide } = showUpwageNotification(
      <Flex className="max-w-72 text-uw-gray-9" direction="column">
        <Text className="font-bold" size="xs">
          {SHARED.title}
        </Text>
        <Text size="xs">{SHARED.content}</Text>
        <Link
          className="font-semibold mt-1.5 text-xs uppercase"
          href={SHARED.link.href}
        >
          {SHARED.link.text}
        </Link>
      </Flex>,
      NotificationTypes.Error,
      {
        autoClose: false,
        classNames: {
          icon: NOTIFICATION_CLASS_NAMES.icon,
          root: twMerge(NOTIFICATION_CLASS_NAMES.root, "items-start"),
        },
      },
    );

    return () => {
      hide();
    };
  }, []);

  return (
    <ScreenerBuilderWrapper {...wrapperProps} hideBottomBar isLoading={false}>
      <Flex
        align="center"
        className="h-full w-full"
        direction="column"
        gap="lg"
        justify="center"
      >
        <Image
          alt=""
          height={185}
          role="presentation"
          src="/assets/error-bot.svg"
          width={150}
        />
        <Flex align="center" direction="column" gap="xs">
          <Title order={5}>{SHARED.title}</Title>
          <Text className="text-center w-80" size="sm">
            {SHARED.content}
          </Text>
        </Flex>
        <Button component={Link} href={SHARED.link.href}>
          {SHARED.link.text}
        </Button>
      </Flex>
    </ScreenerBuilderWrapper>
  );
};
